import { useState, useCallback } from 'react'
import { filterPayeesForSearch, omitUnusedSearchPayeeFields, SearchPayee, TspType } from '@entities/tsp'
import { getPcekZone } from '@entities/user'
import { categoryRequestParams, MESSAGE, reqHeaders } from '@shared/config/constants'
import { catalogManagerApi } from '@root/api/api'
import { useToast } from '@shared/lib/toast'

export const useCategoryServices = () => {
  const [categoryServices, setCategoryServices] = useState<SearchPayee[]>([])
  const [tabsServices, setTabsServices] = useState<Record<TspType, SearchPayee[]> | null>(null)
  const [servicesAll, setServicesAll] = useState<SearchPayee[]>([])
  const [isCategoryServicesLoading, setIsCategoryServicesLoading] = useState<boolean>(true)
  const zone = getPcekZone()

  const { toast } = useToast()

  const fetchCategoryData = useCallback(() => {
    const { requestId, categoryName, showHidden, showFromHiddenCategories } = categoryRequestParams

    setIsCategoryServicesLoading(true)

    catalogManagerApi
      .getPayees(zone, undefined, undefined, categoryName, showHidden, showFromHiddenCategories, {
        headers: { ...reqHeaders, 'Request-Id': requestId },
      })
      .then(({ data }) => {
        const payees = data.payees || []

        const servicesForSearch = filterPayeesForSearch(payees).sort((a, b) => {
          const orderA = a.features?.displayOrder ?? Number.MAX_SAFE_INTEGER
          const orderB = b.features?.displayOrder ?? Number.MAX_SAFE_INTEGER

          return orderA - orderB
        })

        const slimServices = omitUnusedSearchPayeeFields(servicesForSearch)

        setCategoryServices(slimServices)
        setServicesAll(slimServices)

        const displayedServices = servicesForSearch.reduce((acc, service) => {
          service?.features?.category_web?.split(', ').forEach((category) => {
            if (Object.values(TspType).includes(category as TspType)) {
              acc[category] = acc[category] || []
              acc[category].push(service)
            }
          })

          return acc
        }, {}) as Record<TspType, typeof servicesForSearch>

        setTabsServices(
          Object.fromEntries(
            Object.entries(displayedServices).map(([type, payees]) => [type, omitUnusedSearchPayeeFields(payees)])
          ) as Record<TspType, SearchPayee[]>
        )
      })
      .catch((error) => {
        if (error.code !== 'ERR_CANCELED') {
          toast({ variant: 'error', title: MESSAGE, description: '' })
        }
      })
      .finally(() => setIsCategoryServicesLoading(false))
  }, [])

  return {
    categoryServices,
    tabsServices,
    servicesAll,
    isCategoryServicesLoading,
    fetchCategoryData,
  }
}
